import { createFileRoute, useNavigate } from '@tanstack/react-router'
import * as v from 'valibot'
import { useEffect, useState } from 'react'
import { useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { useLingui } from '@lingui/react/macro'
import { Test } from '@/routes/_app/-components/applicants/test'
import { RegistrationSteps } from '@/routes/_app/-components/shared/registration-steps'
import { useAppStore } from '@/lib/app-store'
import { withViewTransition } from '@/lib/utils'
import { queryClient, trpc, type User } from '@/main'
import { FlowFrame } from '@/routes/_app/-components/shared/flow-frame'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { TestStepPending } from '@/routes/_app/-components/shared/flow-pending'

export const Route = createFileRoute('/_app/test/competences')({
  validateSearch: v.object({
    retake: v.optional(v.boolean(), false),
    resume: v.optional(v.boolean()),
    // Carried through onboarding so the final landing can return to the visitor's intended destination.
    redirect: v.optional(v.string()),
  }),
  loaderDeps: ({ search }) => ({ retake: search.retake }),
  loader: ({ context, deps }) => {
    context.queryClient.prefetchQuery(
      trpc.applicants.getQuestions.queryOptions({ type: 'competences', newTest: deps.retake }),
    )
  },
  component: RouteComponent,
  pendingComponent: TestStepPending,
})

function RouteComponent() {
  const navigate = useNavigate()
  const { retake, redirect } = Route.useSearch()
  const { data: user } = useSuspenseQuery(trpc.auth.getUser.queryOptions())

  // Logged-out → /login is enforced centrally by the `_app` layout guard (see route-access), before this
  // route's auth-only `getQuestions` query mounts. The competences question set depends on whether the applicant has had people in their charge, and
  // the server won't serve questions until that's set. It's normally collected on the registration
  // form, but social logins auto-register without it, so capture it here as its own step before the
  // test loads.
  if (user?.type === 'applicant' && user.peopleInCharge == null) {
    return <PeopleInChargeGate user={user} />
  }

  return (
    <Test
      type="competences"
      nextStep={() => navigate({ to: '/test/values', search: { redirect }, viewTransition: true })}
      previousStep={() => navigate({ to: '/test', search: { redirect } })}
      newTest={retake}
    />
  )
}

// The "people in charge" step — shown only while the answer is missing, right after the welcome. On
// answer it persists `peopleInCharge`, refetches the user, and the route re-renders straight into the
// competences test (the next step) — it no longer bounces back to the welcome screen.
function PeopleInChargeGate(props: { user: User }) {
  const { t } = useLingui()
  const [pending, setPending] = useState<boolean | null>(null)
  const markPeopleStepShown = useAppStore((state) => state.markPeopleStepShown)
  const { mutateAsync: updateUser } = useMutation(trpc.auth.updateUser.mutationOptions())

  // Latch the step so it stays visible (as completed) in the indicator once answered.
  useEffect(() => {
    markPeopleStepShown()
  }, [markPeopleStepShown])

  const save = async (peopleInCharge: boolean) => {
    if (pending != null) return
    setPending(peopleInCharge)
    try {
      await updateUser({ peopleInCharge })
      // The route loader prefetched `getQuestions` while peopleInCharge was still null, so it's cached as
      // the value-shaped `{ status: 'profile-not-defined' }` (the server withholds questions until the
      // answer exists). Now that it's set, that cache is stale — <Test> renders null for any non-'ok'
      // status, so without this the questionnaire would be blank. Invalidate it so it refetches real
      // questions before <Test> reads it. `await` so the refetch is in flight as the route re-renders.
      await queryClient.invalidateQueries(trpc.applicants.getQuestions.queryFilter())
      // This is a SAME-ROUTE data swap (not a navigation), so drive the transition ourselves: flip the
      // cached user inside a view transition and the route re-renders straight into the competences
      // <Test> with the same box/header morph as every other onboarding step. Reconcile in background.
      withViewTransition(() => {
        queryClient.setQueryData(trpc.auth.getUser.queryKey(), (old) => (old ? { ...old, peopleInCharge } : old))
      })
      void queryClient.invalidateQueries(trpc.auth.getUser.queryFilter())
    } catch {
      setPending(null)
    }
  }

  return (
    <section className="flex flex-col gap-3">
      <div className="mx-auto flex w-full max-w-[480px] flex-col items-center gap-6 px-4 py-6">
        <RegistrationSteps user={props.user} stage={{ kind: 'people' }} />
        <div className="flow-header text-center">
          <h1 className="text-2xl font-semibold">{t`One quick question`}</h1>
          <p className="text-muted-foreground mt-1">{t`We'll use this to tailor the questionnaire to your experience`}</p>
        </div>

        <FlowFrame contentClassName="flex flex-col gap-5">
          <p className="text-center text-base font-medium">{t`Have you had people in your charge?`}</p>
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              onClick={() => void save(false)}
              disabled={pending != null}
              className="h-12 text-base font-semibold"
            >
              {pending === false && <Spinner />}
              {t`No`}
            </Button>
            <Button onClick={() => void save(true)} disabled={pending != null} className="h-12 text-base font-semibold">
              {pending === true && <Spinner />}
              {t`Yes`}
            </Button>
          </div>
        </FlowFrame>
      </div>
    </section>
  )
}
